import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format, subDays } from 'date-fns'
import {
  Users, UserCheck, UserX, UserMinus,
  RefreshCw,
} from 'lucide-react'
import SectionCard from '@/components/dashboard/SectionCard'
import StatTile from '@/components/dashboard/StatTile'
import BarListWidget from '@/components/dashboard/BarListWidget'
import ListWidget, { type ListWidgetRow } from '@/components/dashboard/ListWidget'
import DetailModal from '@/components/dashboard/DetailModal'
import TrendChart from '@/components/dashboard/TrendChart'
import CheckInOutCard from '@/components/dashboard/CheckInOutCard'
import LeaveSummaryCard from '@/components/dashboard/LeaveSummaryCard'
import LiveClock from '@/components/dashboard/LiveClock'
import { getMyBalance } from '@/api/leaves'
import {
  getOverview, getAttendanceDrilldown, getPeopleSnapshot, getHeadcountMembers,
  getAttendanceSummary, getAttendanceTimeseries,
  type AttendanceDrilldownTiles, type AttendanceDrilldownCategory,
  type AttendanceSummaryTotals, type AttendanceTimeseriesPoint,
  type DrilldownPerson, type SnapshotPerson, type SnapshotJoiner, type SnapshotAnniversary,
  type HeadcountMember, type HeadcountDimension,
} from '@/api/analytics'
import { listUsers } from '@/api/users'

const LEAVE_TYPE_LABELS: Record<string, string> = {
  casual: 'Casual Leave', sick: 'Sick Leave', urgent: 'Urgent Leave', annual: 'Annual Leave', wfh: 'Work From Home',
}

/** What the shared detail modal is currently showing: an already-loaded row
 * list, or one of two lazily-fetched sources (a Head Count bucket, or the
 * full active-employee roster). One modal, three ways to fill it. */
type DetailSelection =
  | { kind: 'rows'; title: string; rows: ListWidgetRow[] }
  | { kind: 'headcount'; title: string; dimension: HeadcountDimension; value: string }
  | { kind: 'active-employees'; title: string }

function drilldownSubtitle(category: AttendanceDrilldownCategory, m: HeadcountMember): string | undefined {
  if (m.minutes != null && category === 'late_arrivals') {
    return `${m.minutes}m late${m.checkin_time ? ` · in ${fmtTime(m.checkin_time)}` : ''}`
  }
  if (m.minutes != null && category === 'early_left') {
    return `${m.minutes}m early${m.checkout_time ? ` · out ${fmtTime(m.checkout_time)}` : ''}`
  }
  if (m.leave_type) return LEAVE_TYPE_LABELS[m.leave_type] || m.leave_type
  if (m.checkin_time) return `In ${fmtTime(m.checkin_time)}${m.checkout_time ? ` · Out ${fmtTime(m.checkout_time)}` : ''}`
  return undefined
}

function headcountMemberRows(members: HeadcountMember[]): ListWidgetRow[] {
  return members.map((m) => ({
    id: m.user_id, name: m.name, avatar_url: m.avatar_url,
    subtitle: [m.department, m.designation].filter(Boolean).join(' · ') || undefined,
  }))
}

export default function Dashboard() {
  const today = format(new Date(), 'yyyy-MM-dd')
  const monthStart = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd')
  const yearStart = format(subDays(new Date(), 364), 'yyyy-MM-dd')

  const overview = useQuery({ queryKey: ['analytics', 'overview'], queryFn: getOverview })
  const drilldown = useQuery({
    queryKey: ['analytics', 'attendance-drilldown', today],
    queryFn: () => getAttendanceDrilldown(today),
    refetchInterval: 60_000,
  })
  const snapshot = useQuery({ queryKey: ['analytics', 'people-snapshot'], queryFn: () => getPeopleSnapshot(30) })
  const monthSummary = useQuery({
    queryKey: ['analytics', 'attendance-summary', monthStart, today],
    queryFn: () => getAttendanceSummary(monthStart, today),
  })
  const monthlyTrend = useQuery({
    queryKey: ['analytics', 'attendance-timeseries', 'month', yearStart, today],
    queryFn: () => getAttendanceTimeseries(yearStart, today, 'month'),
  })
  const myBalance = useQuery({ queryKey: ['leaves', 'balance', 'me'], queryFn: () => getMyBalance() })

  const tiles = drilldown.data?.tiles
  const [detail, setDetail] = useState<DetailSelection | null>(null)

  const openTile = (category: AttendanceDrilldownCategory, title: string) => {
    const members: HeadcountMember[] = drilldown.data?.members?.[category] || []
    setDetail({
      kind: 'rows', title,
      rows: members.map((m: HeadcountMember) => ({
        id: m.user_id, name: m.name, avatar_url: m.avatar_url,
        subtitle: drilldownSubtitle(category, m),
      })),
    })
  }
  const openRows = (title: string, rows: ListWidgetRow[]) => setDetail({ kind: 'rows', title, rows })
  const openHeadcount = (title: string, dimension: HeadcountDimension, value: string) =>
    setDetail({ kind: 'headcount', title, dimension, value })

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <DrilldownSection
        date={today}
        tiles={tiles}
        loading={drilldown.isLoading}
        onRefresh={() => drilldown.refetch()}
        onSelectTile={openTile}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Early Risers">
          <ListWidget rows={toPeopleRows(drilldown.data?.early_risers, (p) => `${p.checkin_time ? fmtTime(p.checkin_time) : ''} · ${p.minutes}m early`)} />
        </SectionCard>
        <SectionCard title="Late Comers">
          <ListWidget rows={toPeopleRows(drilldown.data?.late_comers, (p) => `${p.checkin_time ? fmtTime(p.checkin_time) : ''} · ${p.minutes}m late`)} />
        </SectionCard>
      </div>

      {/* Bottom KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Users className="h-5 w-5" />} tone="green" value={overview.data?.headcount.active ?? '—'} label="Active Employees"
          onClick={() => setDetail({ kind: 'active-employees', title: 'Active Employees' })} />
        <KpiCard icon={<UserMinus className="h-5 w-5" />} tone="teal" value={snapshot.data?.separations_30d.length ?? '—'} label="Separations In 30 Days"
          onClick={() => openRows('Separations In 30 Days', toPersonRows(snapshot.data?.separations_30d))} />
        <KpiCard icon={<UserCheck className="h-5 w-5" />} tone="amber" value={snapshot.data?.confirmations_30d.length ?? '—'} label="Confirmations In 30 Days"
          onClick={() => openRows('Confirmations In 30 Days', toPersonRows(snapshot.data?.confirmations_30d))} />
        <KpiCard icon={<UserX className="h-5 w-5" />} tone="red" value={snapshot.data?.resignations_this_month.length ?? '—'} label="Resignations This Month"
          onClick={() => openRows('Resignations This Month', toPersonRows(snapshot.data?.resignations_this_month))} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard title="Mark Clock In/Clock Out" className="lg:col-span-1">
          <CheckInOutCard />
        </SectionCard>
        <SectionCard title="Leave Summary" className="lg:col-span-1">
          <LeaveSummaryCard balances={myBalance.data} />
        </SectionCard>
        <SectionCard title="Clock" className="lg:col-span-1" bodyClassName="flex items-center justify-center">
          <LiveClock />
        </SectionCard>
      </div>

      <SectionCard title="Attendance Summary" action={<span className="text-xs text-gray-400">{format(new Date(monthStart), 'MMM yyyy')} – {format(new Date(today), 'MMM yyyy')}</span>}>
        <MonthSummaryStrip totals={monthSummary.data?.totals} />
      </SectionCard>

      <SectionCard title="Monthly Attendance Report" action={<span className="text-xs text-gray-400">Last 12 months</span>}>
        <TrendChart
          type="area"
          data={(monthlyTrend.data || []).map((p: AttendanceTimeseriesPoint) => ({ label: p.label, Present: p.present, Absent: p.absent, Leave: p.leave }))}
          series={[
            { key: 'Present', label: 'Presents', color: '#22c55e' },
            { key: 'Absent', label: 'Absents', color: '#ef4444' },
            { key: 'Leave', label: 'Leaves', color: '#f59e0b' },
          ]}
        />
      </SectionCard>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Birthday">
          <ListWidget rows={toPersonRows(snapshot.data?.birthdays)} />
        </SectionCard>
        <SectionCard title="My Team">
          <ListWidget rows={toPersonRows(snapshot.data?.my_team)} emptyLabel="No direct reports" />
        </SectionCard>
        <SectionCard title="New Joiners">
          <ListWidget rows={toJoinerRows(snapshot.data?.new_joiners)} />
        </SectionCard>
        <SectionCard title="Upcoming Work Anniversary">
          <ListWidget rows={toAnniversaryRows(snapshot.data?.upcoming_anniversaries)} />
        </SectionCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Head Count By Department">
          <BarListWidget data={overview.data?.headcount.by_department || []}
            onSelect={(label) => openHeadcount(`Department · ${label}`, 'department', label)} />
        </SectionCard>
        <SectionCard title="Head Count By Designation">
          <BarListWidget data={overview.data?.headcount.by_designation || []}
            onSelect={(label) => openHeadcount(`Designation · ${label}`, 'designation', label)} />
        </SectionCard>
        <SectionCard title="Head Count By Age">
          <BarListWidget data={overview.data?.headcount.by_age || []}
            onSelect={(label) => openHeadcount(`Age · ${label}`, 'age', label)} />
        </SectionCard>
        <SectionCard title="Head Count By Employment Type">
          <BarListWidget data={overview.data?.headcount.by_employment_type || []}
            onSelect={(label) => openHeadcount(`Employment Type · ${label}`, 'employment_type', label)} />
        </SectionCard>
        <SectionCard title="Gender Wise Head Count">
          <BarListWidget data={overview.data?.headcount.by_gender || []}
            onSelect={(label) => openHeadcount(`Gender · ${label}`, 'gender', label)} />
        </SectionCard>
      </div>

      {detail && <DetailModalHost selection={detail} onClose={() => setDetail(null)} />}
    </div>
  )
}

/* ─────────────────────────── Attendance drill-down ─────────────────────────── */

const DRILLDOWN_TILE_DEFS: { key: AttendanceDrilldownCategory; label: string; tone: import('@/components/dashboard/StatTile').Tone }[] = [
  { key: 'active_employees', label: 'Active Employees', tone: 'green' },
  { key: 'planned_shifts', label: 'Planned Shifts', tone: 'gray' },
  { key: 'upcoming_shifts', label: 'Upcoming Shifts', tone: 'blue' },
  { key: 'presents', label: 'Presents', tone: 'green' },
  { key: 'absents', label: 'Absents', tone: 'red' },
  { key: 'holiday', label: 'Holiday', tone: 'teal' },
  { key: 'leaves', label: 'Leaves', tone: 'amber' },
  { key: 'days_off', label: 'Days Off', tone: 'indigo' },
  { key: 'wfh', label: 'WFH', tone: 'purple' },
  { key: 'late_arrivals', label: 'Late Arrivals', tone: 'orange' },
  { key: 'early_left', label: 'Early Left', tone: 'pink' },
]

function DrilldownSection({
  date, tiles, loading, onRefresh, onSelectTile,
}: {
  date: string
  tiles?: AttendanceDrilldownTiles
  loading: boolean
  onRefresh: () => void
  onSelectTile: (category: AttendanceDrilldownCategory, label: string) => void
}) {
  return (
    <SectionCard
      title="Attendance Drill Down"
      action={
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            title="Refresh"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <span className="text-xs text-gray-500">{format(new Date(`${date}T00:00:00`), 'MMMM d, yyyy')}</span>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {DRILLDOWN_TILE_DEFS.map(({ key, label, tone }) => (
          <StatTile
            key={key}
            label={label}
            value={tiles?.[key] ?? '—'}
            tone={tone}
            onClick={() => onSelectTile(key, label)}
          />
        ))}
      </div>
    </SectionCard>
  )
}

/* ─────────────────────────── Bottom KPI cards ─────────────────────────── */

function KpiCard({ icon, tone, value, label, onClick }: {
  icon: ReactNode; tone: 'green' | 'teal' | 'amber' | 'red'; value: ReactNode; label: string; onClick?: () => void
}) {
  const toneBg: Record<string, string> = {
    green: 'bg-green-100 text-green-600', teal: 'bg-teal-100 text-teal-600',
    amber: 'bg-amber-100 text-amber-600', red: 'bg-red-100 text-red-600',
  }
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5 flex items-center gap-4 text-left w-full ${
        onClick ? 'hover:-translate-y-0.5 hover:shadow-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40' : ''
      }`}
    >
      <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${toneBg[tone]}`}>{icon}</span>
      <div className="min-w-0">
        <p className="text-xl sm:text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
        <p className="text-xs sm:text-sm text-gray-500 truncate">{label}</p>
      </div>
    </Comp>
  )
}

/* ─────────────────────────── Check-in/out card ─────────────────────────── */

/* ─────────────────────────── Month summary strip ─────────────────────────── */

function MonthSummaryStrip({ totals }: { totals?: AttendanceSummaryTotals }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      <StatTile label="Shifts" value={totals?.scheduled_working_days ?? '—'} tone="green" />
      <StatTile label="Worked Hours" value={totals ? `${totals.worked_hours}h` : '—'} tone="blue" />
      <StatTile label="Overtime Hours" value={totals ? `${totals.overtime_hours}h` : '—'} tone="indigo" />
      <StatTile label="Leaves" value={totals?.leave_days ?? '—'} tone="amber" />
      <StatTile label="WFH" value={totals?.wfh_days ?? '—'} tone="purple" />
      <StatTile label="Absents" value={totals?.absent_days ?? '—'} tone="red" />
    </div>
  )
}

/* ─────────────────────────── Helpers ─────────────────────────── */

function fmtTime(iso: string): string {
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
}
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}

function toPeopleRows(
  people: DrilldownPerson[] | undefined,
  subtitle: (p: DrilldownPerson) => string,
): ListWidgetRow[] {
  return (people || []).map((p) => ({ id: p.user_id, name: p.name, subtitle: subtitle(p) }))
}

function toPersonRows(people: SnapshotPerson[] | undefined): ListWidgetRow[] {
  return (people || []).map((p) => ({ id: p.user_id, name: p.name, avatar_url: p.avatar_url }))
}

function toJoinerRows(people: SnapshotJoiner[] | undefined): ListWidgetRow[] {
  return (people || []).map((p) => ({
    id: p.user_id, name: p.name, avatar_url: p.avatar_url,
    subtitle: `Joined ${format(new Date(p.date_of_joining), 'MMM d, yyyy')}`,
  }))
}

function toAnniversaryRows(people: SnapshotAnniversary[] | undefined): ListWidgetRow[] {
  return (people || []).map((p) => ({
    id: p.user_id, name: p.name, avatar_url: p.avatar_url,
    subtitle: `${p.years}${ordinal(p.years)} anniversary`,
  }))
}

/* ─────────────────────────── Detail modal ─────────────────────────── */

// This page's own data-fetching wrapper around the shared DetailModal shell
// (components/dashboard/DetailModal.tsx): each `DetailSelection` variant
// supplies a title plus either rows it already has, or which lazy query to
// run — the modal itself is generic and shared with HR Analytics.
function DetailModalHost({ selection, onClose }: { selection: DetailSelection; onClose: () => void }) {
  const dimension = selection.kind === 'headcount' ? selection.dimension : null
  const value = selection.kind === 'headcount' ? selection.value : null

  const headcountQuery = useQuery({
    queryKey: ['analytics', 'headcount-members', dimension, value],
    queryFn: () => getHeadcountMembers(dimension as HeadcountDimension, value as string),
    enabled: selection.kind === 'headcount',
  })
  const activeEmployeesQuery = useQuery({
    queryKey: ['users', 'active-for-drilldown'],
    queryFn: () => listUsers({ is_active: true, per_page: 500 }), // backend caps per_page at 500
    enabled: selection.kind === 'active-employees',
  })

  const loading =
    (selection.kind === 'headcount' && headcountQuery.isLoading) ||
    (selection.kind === 'active-employees' && activeEmployeesQuery.isLoading)

  const rows: ListWidgetRow[] =
    selection.kind === 'rows' ? selection.rows
    : selection.kind === 'headcount' ? headcountMemberRows(headcountQuery.data || [])
    : (activeEmployeesQuery.data?.items || []).map((u: { id: string; name: string; avatar_url?: string }) => ({ id: u.id, name: u.name, avatar_url: u.avatar_url }))

  return <DetailModal title={selection.title} rows={rows} loading={loading} onClose={onClose} />
}
